import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter, map, take } from 'rxjs/operators';
import { ThemeHandlerService } from './services/theme-handler/theme-handler.service';
import {
	ContentModalService,
	ToastService,
	SidebarModule,
	SidebarTopModule,
	SidebarItemModule,
	SidebarBottomModule,
} from '@msi/cobalt';
import { SignalRService } from './services/signalr/signalr.service';
import { ViewsService } from './services/views/views.service';
import { CcAdminApiService } from './services/ccAdmin/cc-admin-api.service';
import { SelectWorkspaceModalComponent } from './components/select-workspace-modal/select-workspace-modal.component';
import { ViewConfigModalComponent } from './components/view-config-modal/view-config-modal.component';
import { ViewModel } from './interfaces/views.interface';
import { AgreementsModalInvokerComponent } from './components/agreements-modal-invoker/agreements-modal-invoker.component';
import { IconModule } from '@msi/cobalt/icon';
import { TooltipModule } from '@msi/cobalt/tooltip';
import { DropdownModule, DropdownItemModule } from '@msi/cobalt/dropdown';
import { SupportInfoComponent } from './components/support-info/support-info.component';
import {
	HeaderComponentModule,
	HeaderLeftPluginAreaComponentModule,
	HeaderRightPluginAreaComponentModule,
	ApplicationSwitcherComponentModule,
	SessionManagerComponentModule,
} from '@msi/commandcentral-common-header';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	standalone: true,
	imports: [
		HeaderComponentModule,
		HeaderLeftPluginAreaComponentModule,
		HeaderRightPluginAreaComponentModule,
		ApplicationSwitcherComponentModule,
		SupportInfoComponent,
		SessionManagerComponentModule,
		SidebarModule,
		SidebarTopModule,
		SidebarItemModule,
		DropdownModule,
		TooltipModule,
		DropdownItemModule,
		IconModule,
		RouterLink,
		SidebarBottomModule,
		RouterOutlet,
		AgreementsModalInvokerComponent,
	],
})
export class AppComponent implements OnInit, AfterViewInit {
	@ViewChild('adBanner', { static: false }) adBanner: ElementRef;
	readonly selfRegistrationUrlPath = 'self-registration-setup';
	readonly noSidebarPages = ['error', 'eula', this.selfRegistrationUrlPath, 'demo', 'sh-demo'];
	readonly adBlockDetectedText = $localize`It looks like you're using an Adblocker. Please disable it as it will cause Orchestrate functionality to be impacted.`;

	currentPage: string;
	showCurrentPageInSidebar: boolean;
	sidebarExpanded = true;

	atLeastOneWorkspace = false;
	canUserEditWorkflow = false;
	canCreateNewWorkspaces: boolean;

	constructor(
		// ThemeHandlerService is not used here, but we want it to be initialized regardless of which page we're on
		private themeHandlerService: ThemeHandlerService,
		// signalRService is not used here, but we want it to be initialized regardless of which page we're on
		private signalRService: SignalRService,
		private router: Router,
		private toastService: ToastService,
		private contentModalService: ContentModalService,
		private ccAdminApiService: CcAdminApiService,
		private viewService: ViewsService
	) {
		this.router.events
			.pipe(
				filter(e => e instanceof NavigationEnd),
				map(e => e as NavigationEnd)
			)
			.subscribe(e => {
				this.currentPage = e.urlAfterRedirects.split(/[/?]+/)[1];
				this.showCurrentPageInSidebar = !this.noSidebarPages.includes(this.currentPage);
			});
	}

	ngOnInit(): void {
		// Load previous state of sidebarExpanded
		this.sidebarExpanded = localStorage.getItem('sidebar_expanded') !== 'false';

		// Determine if the user has permissions to edit Workflows
		this.ccAdminApiService.graphInitialized$.pipe(take(1)).subscribe(() => {
			this.canCreateNewWorkspaces = this.ccAdminApiService.user.canEditAnyWorkspace;
			this.canUserEditWorkflow =
				this.ccAdminApiService.user.canEditWorkflows || this.ccAdminApiService.user.canEditAnyWorkflow;

			this.viewService.views$.subscribe(views => {
				this.atLeastOneWorkspace =
					views.filter((w: ViewModel) => this.ccAdminApiService.user.canEditWorkflowsInWorkspace(w)).length > 0;
			});
		});
	}

	ngAfterViewInit() {
		this.router.events
			.pipe(
				filter(e => e instanceof NavigationEnd),
				map(e => e as NavigationEnd),
				take(1)
			)
			.subscribe(e => {
				const currentPageTemp = e.url.split(/[/?]+/)[1];
				// Check if the ad blocker is enabled and show a toast if it is enabled
				setTimeout(() => {
					if (this.adBanner.nativeElement.offsetHeight === 0 && !this.noSidebarPages.includes(currentPageTemp)) {
						this.toastService.warning(this.adBlockDetectedText, null, { autoDismiss: 7000 });
					}
				}, 2000);
			});
	}

	navigateTo(navigateTo: 'new-workflow' | 'new-workspace' | 'connections') {
		if (navigateTo === 'new-workflow') {
			this.router.navigate(['workflows']).then(() => {
				/*
				 * If the user is on the Workflow edit screen, it means that they had a stale Workflow and they
				 * selected 'Cancel' from the 'Changes you made may not be saved' warning, hence don't open the config modal.
				 * This regex matches any string that has 'workflows/' followed by any characters.
				 * */
				if (RegExp(/workflows\/.+/).exec(this.router.url)?.length > 0) return;
				this.contentModalService.open(SelectWorkspaceModalComponent, {
					data: { selectedMediaIndex: 0 },
				});
			});
		} else if (navigateTo === 'new-workspace') {
			this.router.navigate(['workflows']).then(() => {
				if (RegExp(/workflows\/.+/).exec(this.router.url)?.length > 0) return;
				this.contentModalService.open(ViewConfigModalComponent, {
					data: { viewId: null, selectedMediaIndex: 0 },
				});
			});
		} else {
			this.router.navigate(['connections']);
		}
	}

	toggleSidebarExpansion(expanded: boolean) {
		this.sidebarExpanded = expanded;
		// Cache current state of sidebarExpanded
		localStorage.setItem('sidebar_expanded', expanded.toString());
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
